import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    buildProvisionalGeographyOption,
    Geography,
    mergeGeographyOptions,
    resolveGeographyOption,
    sortOriginGeographyOptions,
} from "src/api/sure-api/geography/geographySchema";
import { useGetOneGeography } from "src/api/sure-api/geography/useGetOneGeography";
import { useOriginGeographyOptions } from "src/api/sure-api/geography/useOriginGeographyOptions";
import { FoodsAsyncComboboxField } from "src/components/EntityForm/FoodsFields";

type IngredientOriginSelectProps = {
    value: string | null | undefined;
    defaultOrigin?: string | null;
    onChange: (origin: string | null) => void;
    disabled?: boolean;
};

const isOriginGeography = (geography: Geography | undefined): geography is Geography =>
    geography != null && (geography.type === "country" || geography.type === "region");

const normalizeSearchValue = (value: string) =>
    value
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .trim()
        .toLocaleLowerCase();

export const IngredientOriginSelect = (props: IngredientOriginSelectProps) => {
    const { value, defaultOrigin, onChange, disabled = false } = props;
    const { t } = useTranslation();
    const seededOriginRef = useRef<string | null>(null);
    const { geographies, isLoading } = useOriginGeographyOptions();

    const effectiveValue = value ?? defaultOrigin ?? null;

    useEffect(() => {
        if (effectiveValue != null && effectiveValue.trim() !== "") {
            return;
        }

        if (defaultOrigin == null || defaultOrigin.trim() === "") {
            return;
        }

        if (seededOriginRef.current === defaultOrigin) {
            return;
        }

        seededOriginRef.current = defaultOrigin;
        onChange(defaultOrigin);
    }, [defaultOrigin, effectiveValue, onChange]);

    const { geography: selectedGeography } = useGetOneGeography(effectiveValue, {
        enabled: effectiveValue != null && effectiveValue.trim() !== "",
    });
    const { geography: defaultGeography } = useGetOneGeography(defaultOrigin, {
        enabled:
            defaultOrigin != null &&
            defaultOrigin.trim() !== "" &&
            defaultOrigin.trim().toUpperCase() !== (effectiveValue ?? "").trim().toUpperCase(),
    });

    const options = useMemo(() => {
        const resolvedSelectedGeography = isOriginGeography(selectedGeography) ? selectedGeography : null;
        const resolvedDefaultGeography = isOriginGeography(defaultGeography) ? defaultGeography : null;

        return sortOriginGeographyOptions(
            mergeGeographyOptions(geographies, [resolvedSelectedGeography, resolvedDefaultGeography]),
        );
    }, [defaultGeography, geographies, selectedGeography]);

    const selectedOption = useMemo(() => {
        const resolved = resolveGeographyOption(effectiveValue, options);
        if (resolved !== null) {
            return resolved;
        }

        if (effectiveValue == null || effectiveValue.trim() === "") {
            return null;
        }

        return buildProvisionalGeographyOption(effectiveValue);
    }, [effectiveValue, options]);

    const [inputValue, setInputValue] = useState(selectedOption?.name ?? "");

    useEffect(() => {
        setInputValue(selectedOption?.name ?? "");
    }, [selectedOption]);

    const selectableOptions = useMemo(
        () => mergeGeographyOptions(options, [selectedOption]),
        [options, selectedOption],
    );

    return (
        <FoodsAsyncComboboxField<Geography>
            label={t("product.v2.recipe.origin", "Origin")}
            placeholder={t("geography.select.placeholder", "Search geography by code or name (e.g. FR, France)")}
            value={selectedOption}
            inputValue={inputValue}
            options={selectableOptions}
            filterOptions={(availableOptions, searchValue) => {
                const normalizedInput = normalizeSearchValue(searchValue);

                return normalizedInput === "" ?
                        [...availableOptions]
                    :   availableOptions.filter((option) =>
                            normalizeSearchValue(option.name).includes(normalizedInput),
                        );
            }}
            getOptionId={(option) => option.code}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, selectedValue) => option.code === selectedValue.code}
            onInputChange={setInputValue}
            onOptionChange={(option) => {
                setInputValue(option?.name ?? "");
                onChange(option?.code ?? null);
            }}
            disabled={disabled || isLoading}
            loading={isLoading}
            loadingText={t("common.loading", "Loading...")}
            noOptionsText={t("component.select.noresultfound", "No results found")}
            clearLabel={t("common.clear", "Clear")}
            openLabel={t("component.select.open", "Open options")}
            closeLabel={t("common.close", "Close")}
            freeSolo={false}
            clearOnBlur
            openOnFocus={false}
            selectOnFocus
            appearance="inline"
        />
    );
};
